import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2Icon } from "lucide-react";

/**
 * The confirmation after registering.
 *
 * A dedicated page rather than a toast, because "we got it, here's what
 * happens next, and here's what you can do meanwhile" does not fit in one and
 * because a toast is gone before somebody has finished reading it. This is the
 * screen that stops the "did that go through?" support email.
 */
export const Route = createFileRoute("/businesses/registered/$businessSlug")({
	component: RegisteredPage,
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(
			context.trpc.businesses.bySlug.queryOptions({
				slug: params.businessSlug,
			}),
		),
});

function RegisteredPage() {
	const { businessSlug } = Route.useParams();
	const trpc = useTRPC();
	const business = useQuery(
		trpc.businesses.bySlug.queryOptions({ slug: businessSlug }),
	);

	if (business.isPending) {
		return (
			<div className="mx-auto max-w-xl px-4 py-16">
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	const name = business.data?.name ?? "Your business";

	return (
		<div className="mx-auto max-w-xl px-4 py-16 text-center">
			<div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10">
				<CheckCircle2Icon className="size-6 text-primary" aria-hidden="true" />
			</div>

			<h1 className="mt-6 font-semibold text-2xl tracking-tight text-balance">
				{name} is being reviewed
			</h1>

			<p className="mt-3 text-muted-foreground text-pretty">
				A member of the Joint Development Authority will review it, usually
				within two business days. We've emailed you a copy of this, and we'll
				email you as soon as there's a decision.
			</p>

			<div className="mt-8 rounded-lg border border-dashed p-4 text-left">
				<p className="font-medium text-sm">You don't have to wait to start</p>
				<p className="mt-1 text-muted-foreground text-sm text-pretty">
					Draft your first listing now. Drafts stay private, and the moment{" "}
					{name} is approved you can publish them in one click.
				</p>
			</div>

			<div className="mt-6 flex flex-wrap justify-center gap-3">
				<Button asChild>
					<Link
						to="/dashboard/$businessSlug/jobs/new"
						params={{ businessSlug }}
					>
						Draft a listing
					</Link>
				</Button>
				<Button asChild variant="outline">
					<Link to="/dashboard/$businessSlug" params={{ businessSlug }}>
						Go to {name}
					</Link>
				</Button>
			</div>
		</div>
	);
}
